<template>
  <div class="pt-4">
    <div class="page-header inner py-0">
      <h1>Client Management</h1>

      <ViewFilter
        class="view-filter filter"
        :filters="filters"
        :applied-filters="appliedFilters"
        @change="handleFilterChange" />

      <label class="search-bar input input-sm flex min-w-96 items-center gap-2 place-self-end self-center">
          <input
            ref="searchInputRef"
            type="text"
            placeholder="Search"
            class="grow"
            v-model="searchValue"
            @keydown.escape.stop="clearSearch"
            @keyup="handleSearchKeyUp" />
          <XIcon
            v-if="this.searchValue.length > 0"
            :size="20"
            class="cursor-pointer"
            @click="clearSearch" />
          <SearchIcon
            :size="20"
            class="cursor-pointer"
            @click="executeSearch" />
        </label>
    </div>
    <div class="flex justify-end pt-2 pr-4 gap-4">
      <label
        for="toggle-current-only"
        class="label flex items-center justify-end">
        <input
          id="toggle-current-only"
          type="checkbox"
          v-model="showCurrentOnly"
          :checked="showCurrentOnly"
          class="toggle border border-solid border-gray-400 toggle-md toggle-secondary" />

        <span class="cursor-pointer select-none"> Show Current Only </span>
      </label>
      <button
        class="btn btn-sm border border-gray-300"
        @click="downloadCSV"
        title="Download CSV">
        <Download class="h-4 w-4 stroke-primary" />
      </button>
    </div>

    <div class="inner pt-0 w-full">
      <div
        v-if="!loading && error"
        class="alert alert-error border-t">
        {{ error }}
      </div>
      <div v-if="loading && !error" class="w-full min-w-0 space-y-3">
        <div
          v-for="i in 8"
          :key="i"
          class="flex w-full min-w-0 items-center gap-4 py-2 px-2 rounded-md"
        >
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton shape="rectangle" height="1rem" width="100%" />
            <Skeleton shape="rectangle" height="0.75rem" width="100%" />
          </div>
        </div>
      </div>
      <div v-if="page.accounts && page.accounts.length > 0">
        <table
          class="apptable sortable dense --tight-first mt-2"
          v-if="!loading && !error">
          <thead :style="{ top: `${this.stickyTop}px` }">
            <tr>
              <td class="nosort rounded-tl-md rounded-bl-md">&nbsp;</td>
              <td
                :class="sortClassName('accountName')"
                @click="sort('accountName')">
                Name
              </td>
              <td class="nosort">CAC Entity</td>
              <td class="nosort">Coverages</td>
              <td
                :class="sortClassName('inForcePremium')"
                class="text-right"
                @click="sort('inForcePremium')">
                In-Force Premium
              </td>
              <td
                :class="sortClassName('revenue')"
                class="text-right"
                @click="sort('revenue')">
                Revenue
              </td>
              <td
                v-if="!isExternalClient"
                :class="sortClassName('pipeline')"
                class="text-right"
                @click="sort('pipeline')">
                Pipeline
              </td>
              <td
                v-if="!isExternalClient"
                :class="sortClassName('topLevelIndustry')"
                @click="sort('topLevelIndustry')">
                Industry
              </td>
              <td
                :class="sortClassName('parentAccountName')"
                @click="sort('parentAccountName')">
                Parent Company
              </td>
              <td
                :class="sortClassName('insertedDate')"
                @click="sort('insertedDate')">
                Client Since
              </td>
              <td class="actions rounded-tr-md rounded-br-md">&nbsp;</td>
            </tr>
          </thead>

          <tbody :class="{ 'context-menu-open': openContextMenuAccountID !== null }">
            <ContextMenuRoot
              v-for="account in page.accounts"
              :key="account.accountID"
              @update:open="(open) => setContextMenuOpen(account.accountID, open)">
              <ContextMenuTrigger as-child>
                <tr data-test="row">
                  <td>
                    <div class="flex">
                      <FavoriteIndicator
                        tooltip-position="right"
                        size="sm"
                        v-model="account.favorite"
                        @update:model-value="toggleFavorite(account)"
                        :loading="account.progress" />
                    </div>
                  </td>
                  <td>
                    <div class="flex items-center gap-2">
                      <div class="min-w-0 flex-1">
                        <div class="name-wrapper">
                          <router-link
                            class="name text-primary font-semibold"
                            :to="`/account/${account.accountID}/overview`"
                            data-test="account-name">
                            <div v-html="highlight(account.displayName || account.accountName, searchValue)" />
                            <div
                              v-if="account.displayName"
                              v-html="highlight(account.accountName, searchValue)"
                              class="ml-2 text-xs font-light italic" />
                          </router-link>
                        </div>
                        <div class="flex flex-wrap gap-1">
                          <Badge
                            v-if="account.accountStatus === 'P'"
                            class="border-sky-500/80 bg-none font-normal text-sky-500/90"
                            variant="outline">
                            PROSPECT
                          </Badge>
                          <div
                            class="stock flex items-center gap-1.5"
                            v-if="isPublic(account)">
                            <Badge
                              class="border-gray-300 bg-none text-xs font-normal"
                              variant="outline">
                              PUBLIC
                            </Badge>

                            <Badge
                              v-if="account.stockTicker"
                              class="flex shrink-0 items-center gap-1 border-gray-300 whitespace-nowrap"
                              variant="outline">
                              <div
                                class="stock-exchange flex items-center font-normal uppercase"
                                data-test="stock-exchange">
                                {{ account.stockExchange }}
                              </div>
                              <div
                                v-if="account.stockExchange && account.stockTicker"
                                class="flex items-center">
                                :
                              </div>
                              <div
                                class="stock-ticker flex items-center uppercase"
                                data-test="stock-ticker">
                                {{ account.stockTicker }}
                              </div>
                              <div
                                v-if="account.fortuneRank !== undefined && account.fortuneRank !== null"
                                class="tooltip tooltip-right flex items-center"
                                :data-tip="`Fortune 500 Ranking`">
                                <span
                                  class="text-[11px] font-semibold text-amber-700"
                                  data-test="fortune-rank">
                                  #{{ account.fortuneRank }}
                                </span>
                              </div>
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div class="flex shrink-0 items-center gap-2">
                        <div
                          v-if="account.hasExternalUsers"
                          class="tooltip tooltip-top z-50"
                          data-tip="Account has external users with access to it.">
                          <Users
                            :size="16"
                            class="text-gray-600" />
                        </div>
                        <SalesforceLink
                          v-if="!isExternalClient"
                          :sales-force-id="account.salesForceID"
                          :size="20" />
                      </div>
                    </div>
                  </td>
                  <td>
                    <div
                      class="chips m-0 flex flex-wrap gap-1"
                      v-if="account.agencies.length > 0 && account.agencies[0] !== ''">
                      <Badge
                        v-for="agency in account.agencies"
                        :key="agency"
                        variant="outline"
                        class="bg-none font-normal"
                        :class="badgeStyleForAgency(agency)">
                        <div v-html="highlight(agency, searchValue)"></div>
                      </Badge>
                    </div>
                  </td>
                  <td>
                    <div
                      class="chips m-0 flex flex-wrap gap-1"
                      v-if="account.coverageTypes.length > 0">
                      <Badge
                        v-for="{ key, coverageType, isExternal } in account.coverageTypes"
                        :key="key"
                        variant="outline"
                        class="border-gray-300 bg-none font-normal text-primary"
                        :class="{ '': isExternal }">
                        <div v-html="highlight(coverageType, searchValue)"></div>
                      </Badge>
                    </div>
                  </td>
                  <td>
                    <div
                      v-if="account.inForcePremium > 0"
                      data-test="inforce-premium"
                      class="text-right">
                      {{ formatCurrency(account.inForcePremium) }}
                    </div>
                    <div
                      class="empty text-right"
                      v-else>
                      &mdash;
                    </div>
                  </td>
                  <td>
                    <div
                      v-if="account.revenue > 0"
                      data-test="revenue"
                      class="text-right">
                      {{ formatCurrency(account.revenue) }}
                    </div>
                    <div
                      class="empty text-right"
                      v-else>
                      &mdash;
                    </div>
                  </td>
                  <td v-if="!isExternalClient">
                    <div
                      data-test="pipeline"
                      class="text-right">
                      {{ formatCurrency(account.pipeline) }}
                    </div>
                  </td>
                  <td v-if="!isExternalClient">
                    <div
                      class="industry"
                      v-if="account.industryName">
                      <div
                        class="industry-top-level"
                        data-test="top-level-industry">
                        <div v-html="highlight(account.industryName, searchValue)"></div>
                      </div>
                    </div>
                    <div
                      class="empty"
                      v-else>
                      &mdash;
                    </div>
                  </td>
                  <td>
                    <router-link
                      :to="`/parent/account/${account.parentCompanyID}/overview`"
                      v-if="account.parentCompany"
                      data-test="parent-company">
                      <div v-html="highlight(account.parentCompany, searchValue)"></div>
                    </router-link>
                    <div
                      class="empty"
                      v-else>
                      &mdash;
                    </div>
                  </td>
                  <td>
                    <div
                      v-if="account.insertedDate && account.accountStatus !== 'P'"
                      data-test="client-since">
                      {{ formatDate(account.insertedDate) }}
                    </div>
                    <div
                      class="empty"
                      v-else>
                      &mdash;
                    </div>
                  </td>
                  <td class="actions">
                    <router-link
                      v-if="!isExternalClient"
                      :to="`/account/${account.accountID}/edit`"
                      data-test="edit-link"
                      >Edit</router-link
                    >
                  </td>
                </tr>
              </ContextMenuTrigger>
              <ContextMenuPortal to="body">
                <!-- z-index must clear the fixed app header (z-1000): reka copies this element's computed
                     z-index onto the body-level positioning wrapper it teleports the menu into. -->
                <ContextMenuContent
                  class="z-[1100] max-w-80 min-w-60 gap-1 rounded-md bg-white p-2 text-sm outline-1 outline-gray-300 drop-shadow-xl"
                  data-test="context-menu">
                  <ContextMenuItem
                    class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200 data-highlighted:bg-gray-200"
                    data-test="context-open"
                    @select="navigateTo(`/account/${account.accountID}/overview`)">
                    Open
                  </ContextMenuItem>
                  <template v-if="!isExternalClient">
                    <ContextMenuItem
                      class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200 data-highlighted:bg-gray-200"
                      data-test="context-edit"
                      @select="navigateTo(`/account/${account.accountID}/edit`)">
                      Edit
                    </ContextMenuItem>
                    <ContextMenuSeparator class="my-2 h-px bg-gray-300" />
                    <ContextMenuItem
                      as-child
                      :disabled="!salesforceUrl(account)"
                      class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none"
                      :class="salesforceUrl(account) ? 'hover:bg-gray-200 data-highlighted:bg-gray-200' : 'cursor-not-allowed opacity-50'"
                      data-test="context-salesforce">
                      <a
                        :href="salesforceUrl(account) ?? undefined"
                        :title="salesforceUnavailableReason(account) ?? undefined"
                        target="_blank"
                        rel="noopener noreferrer">
                        Open in Salesforce
                      </a>
                    </ContextMenuItem>
                  </template>
                </ContextMenuContent>
              </ContextMenuPortal>
            </ContextMenuRoot>
          </tbody>
        </table>

        <div
          class="paging-controls pt-4"
          v-if="!loading && !error">
          <div class="paging-buttons flex gap-4">
            <AppButton
              secondary
              :disabled="!page.prevPage"
              @click="currentPage--"
              data-test="prev-button">
              <CircleChevronLeft
                :size="20"
                class="mr-2 text-primary"
              />
              Previous
            </AppButton>
            <AppButton
              secondary
              :disabled="!page.nextPage"
              @click="currentPage++"
              data-test="next-button">
              Next
              <CircleChevronRight
                :size="20"
                class="ml-2 text-primary"
              />
            </AppButton>
          </div>
        </div>
      </div>
      <div v-else-if="!loading && !error">
        <EmptyDisplay
          :src="emptySrc"
          alt="No results found"
          title="No results found" />
      </div>
    </div>
  </div>
</template>

<script>
import { accountService, userAccountService, referenceService } from '@/services/api';
import { FailedToLoadPage } from '@/utilities/Errors';
import { DownloadCSV, TransformAccountsToCSV } from '@/utilities/Download';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import SalesforceLink from '@/components/SalesforceLink.vue';
import emptySrc from '@/assets/images/empty.svg';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import { Store } from '@/store';
import { chipColor, agencyChipColors, colors } from '@/utilities/Color';
import { filters } from '@/utilities/Filters';
import Skeleton from '@/components/v2/Skeleton.vue';
import { CircleChevronLeft, CircleChevronRight, Download, SearchIcon, Users, XIcon } from '@lucide/vue';
import { CanAccessSalesforce, IsUserAClient } from '@/utilities/AuthUtils';
import AppButton from '@/components/v2/AppButton.vue';
import Badge from '@/components/ui/Badge.vue';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import ViewFilter from '@/components/ViewFilter.vue';
import { ref } from 'vue';
import { formatDate } from '@/utilities/Format';
import { formatCurrency } from '@/utilities/Money';
import { salesforceAccountUrl, salesforceUnavailableReason } from '@/utilities/Account';
import { ContextMenuRoot, ContextMenuTrigger, ContextMenuPortal, ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from 'reka-ui';

export default {
  name: 'AccountList',
  components: {
    AppButton,
    FavoriteIndicator,
    SalesforceLink,
    EmptyDisplay,
    Skeleton,
    Users,
    SearchIcon,
    XIcon,
    Badge,
    ViewFilter,
    Download,
    CircleChevronLeft,
    CircleChevronRight,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuPortal,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
  },
  setup() {
    const { totalOffset } = useHeaderOffset();
    const filters = ref([
      {
        category: 'Account Status',
        values: [
          {
            label: 'Clients',
            value: 'I',
          },
          {
            label: 'Prospects',
            value: 'P',
          },
        ],
      },
      {
        category: 'Agencies',
        values: [
          {
            label: 'Specialty',
            value: 'Specialty',
          },
          {
            label: 'Agency',
            value: 'Agency',
          },
          {
            label: 'Capital',
            value: 'Capital',
          },
        ],
      },
      {
        category: 'Ownership Type',
        values: [
          {
            label: 'Public',
            value: 'Public',
          },
          {
            label: 'Private',
            value: 'Private',
          },
          {
            label: 'Not Specified',
            value: 'Not Specified',
          },
        ],
      },
    ]);

    // default applied filters
    const appliedFilters = ref([]);

    return { totalOffset, filters, appliedFilters };
  },
  data: () => ({
    list: [],
    filteredList: [],
    loading: true,
    error: undefined,
    currentPage: 0,
    pageSize: 15,
    searchValue: '',
    accountStatusArray: ['I', 'P'],
    agencyArray: ['Specialty', 'Agency', 'Capital'],
    emptySrc,
    sortField: '',
    sortDirection: '',
    stickyTop: 0,
    chipColor,
    agencyChipColors,
    colors,
    showCurrentOnly: false,
    openContextMenuAccountID: null,
  }),
  computed: {
    selectedAccountStatuses() {
      return this.appliedFilters?.find((f) => f.category === 'Account Status')?.values.map((v) => v.value) ?? [];
    },
    selectedAgencies() {
      return this.appliedFilters?.find((f) => f.category === 'Agencies')?.values.map((v) => v.value) ?? [];
    },
    selectedSICIndustries() {
      return this.appliedFilters?.find((f) => f.category === 'SIC Industry')?.values.map((v) => v.value) ?? [];
    },
    selectedIndustries() {
      return this.appliedFilters?.find((f) => f.category === 'Industry')?.values.map((v) => v.value) ?? [];
    },
    selectedCoverageTypes() {
      return this.appliedFilters?.find((f) => f.category === 'Coverage Type')?.values.map((v) => v.value) ?? [];
    },
    selectedOwnershipTypes() {
      return this.appliedFilters?.find((f) => f.category === 'Ownership Type')?.values.map((v) => v.value) ?? [];
    },
    selectedRevenueRange() {
      return this.appliedFilters?.find((f) => f.category === 'Revenue')?.range ?? [];
    },
    revenueMin() {
      return this.selectedRevenueRange && this.selectedRevenueRange.length > 0 && this.selectedRevenueRange[0] !== undefined
        ? Number(this.selectedRevenueRange[0])
        : undefined;
    },
    revenueMax() {
      return this.selectedRevenueRange && this.selectedRevenueRange.length > 1 && this.selectedRevenueRange[1] !== undefined
        ? Number(this.selectedRevenueRange[1])
        : undefined;
    },
    selectedFortuneRankRange() {
      return this.appliedFilters?.find((f) => f.category === 'Fortune Rank')?.range ?? [];
    },
    fortuneRankMin() {
      return this.selectedFortuneRankRange && this.selectedFortuneRankRange.length > 0 && this.selectedFortuneRankRange[0] !== undefined
        ? Number(this.selectedFortuneRankRange[0])
        : undefined;
    },
    fortuneRankMax() {
      return this.selectedFortuneRankRange && this.selectedFortuneRankRange.length > 1 && this.selectedFortuneRankRange[1] !== undefined
        ? Number(this.selectedFortuneRankRange[1])
        : undefined;
    },
    isExternalClient() {
      return IsUserAClient(Store.state.user);
    },
    canAccessSalesforce() {
      return CanAccessSalesforce(Store.state.user);
    },
    page() {
      return {
        prevPage: this.currentPage > 0,
        nextPage: this.filteredList.length > this.pageSize,
        accounts: this.filteredList.length > this.pageSize ? this.filteredList.slice(0, this.filteredList.length - 1) : this.filteredList,
      };
    },
  },
  mounted() {
    this.registerResizeEvent();
    this.updateStickyTop();
    if (this.$refs.searchInputRef) {
      this.$refs.searchInputRef.focus();
    }
  },
  beforeUnmount() {
    this.unregisterResizeEvent();
  },
  methods: {
    setSICIndustries(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'SIC Industry');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'SIC Industry');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setIndustries(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Industries');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Industries');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setAccountStatuses(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Account Status');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Account Status');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setAgencies(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Agencies');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Agencies');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setOwnershipTypes(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Ownership Type');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Ownership Type');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setRevenueRange(range) {
      if (!range || range.length < 2) return;
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Revenue');
      if (filterGroup) {
        filterGroup.range = [Number(range[0]), Number(range[1])];
      } else {
        // If Revenue filter group not present yet, create it with provided range
        this.appliedFilters.push({ category: 'Revenue', values: [], range: [Number(range[0]), Number(range[1])] });
      }
    },
    setFortuneRankRange(range) {
      if (!range || range.length < 2) return;
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Fortune Rank');
      if (filterGroup) {
        filterGroup.range = [Number(range[0]), Number(range[1])];
      } else {
        // If Fortune Rank filter group not present yet, create it with provided range
        this.appliedFilters.push({ category: 'Fortune Rank', values: [], range: [Number(range[0]), Number(range[1])] });
      }
    },
    handleFilterChange(filters) {
      this.appliedFilters = [...filters];
      this.filterAccounts();
    },
    handleSearchKeyUp(e) {
      if (e.key === 'Enter') {
        this.executeSearch();
      }
    },
    executeSearch() {
      if (this.loading) {
        return;
      }
      this.currentPage = 0;
      this.filterAccounts();
    },
    clearSearch() {
      this.searchValue = '';
      this.currentPage = 0;
      this.filterAccounts();
    },
    highlight: filters.highlight,
    formatDate,
    salesforceUrl(account) {
      return this.canAccessSalesforce ? salesforceAccountUrl(account.salesForceID) : null;
    },
    salesforceUnavailableReason(account) {
      return salesforceUnavailableReason(this.canAccessSalesforce, account.salesForceID);
    },
    formatCurrency(value) {
      return formatCurrency(Math.round(value), true);
    },
    updateStickyTop() {
      this.stickyTop = this.totalOffset + 4;
    },
    setContextMenuOpen(accountID, open) {
      if (open) {
        this.openContextMenuAccountID = accountID;
      } else if (this.openContextMenuAccountID === accountID) {
        this.openContextMenuAccountID = null;
      }
    },
    navigateTo(path) {
      // vue-router rejects on redirected/aborted navigations, which the route guards do on purpose.
      this.$router.push(path).catch(() => {});
    },
    registerResizeEvent() {
      window.addEventListener('resize', this.updateStickyTop);
    },
    unregisterResizeEvent() {
      window.removeEventListener('resize', this.updateStickyTop);
    },
    async toggleFavorite(account) {
      const updateListProgress = (list, accountID, progress) => list.map((a) => (a.accountID === accountID ? { ...a, progress } : a));
      const updateListFavorite = (list, accountID, favorite) => list.map((a) => (a.accountID === accountID ? { ...a, favorite } : a));

      this.filteredList = updateListProgress(this.filteredList, account.accountID, true);
      if (!account.favorite) {
        await userAccountService.remove(account.accountID);
        this.filteredList = updateListFavorite(this.filteredList, account.accountID, false);
      } else {
        await userAccountService.create(account.accountID);
        this.filteredList = updateListFavorite(this.filteredList, account.accountID, true);
      }
      this.filteredList = updateListProgress(this.filteredList, account.accountID, false);
    },
    isPublic(account) {
      return account.ownershipType?.toLowerCase() === 'public';
    },
    sort(key) {
      if (this.sortField !== key) {
        this.sortField = key;
        this.sortDirection = 'ascending';
      } else {
        switch (this.sortDirection) {
          case 'ascending':
            this.sortDirection = 'descending';
            break;
          case 'descending':
            this.sortField = '';
            this.sortDirection = '';
            break;
          default:
            this.sortDirection = 'ascending';
        }
      }
      this.filterAccounts();
    },
    badgeStyleForAgency(agency) {
      switch (agency.trim()) {
        case 'Specialty':
          return 'text-specialty-teal/90 border-specialty-teal/80';
        case 'Agency':
          return 'text-agency-red/90 border-agency-red/80';
        case 'Capital':
          return 'text-capital-blue/90 border-capital-blue/80';
        default:
          return '';
      }
    },
    sortClassName(key) {
      if (this.sortField !== key) {
        return null;
      }
      if (this.sortDirection) {
        return { [this.sortDirection]: true, sorted: true };
      }
      return null;
    },
    async downloadCSV() {
      const list = await accountService.search({
        search: this.searchValue,
        agencies: this.selectedAgencies,
        accountStatuses: this.selectedAccountStatuses,
        sicCodes: this.selectedSICIndustries,
        industryIds: this.selectedIndustries,
        coverageTypeIds: this.selectedCoverageTypes,
        ownershipType: this.selectedOwnershipTypes,
        limit: 999_999_999,
        offset: 0,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        showCurrentOnly: this.showCurrentOnly,
        revenueMin: this.revenueMin,
        revenueMax: this.revenueMax,
        fortuneRankMin: this.fortuneRankMin,
        fortuneRankMax: this.fortuneRankMax,
      });

      DownloadCSV(TransformAccountsToCSV(list, this.isExternalClient), 'Accounts');
    },
    async filterAccounts() {
      try {
        this.loading = true;
        this.filteredList = await this.searchAccounts();
      } catch (err) {
        console.error(err);
        this.error = FailedToLoadPage;
      } finally {
        this.loading = false;
      }
    },
    setSearchCriteriaFromQuery() {
      const { a, q, t, p, s, d, si, i, rmin, rmax, ot, frmin, frmax } = this.$route.query;
      if (a || q || t || p || s || d || si || i || rmin || rmax || ot || frmin || frmax) {
        this.setAccountStatuses(t ? [...t] : []);
        this.setAgencies(a ? [...a] : []);
        this.setSICIndustries(si ? [...si] : []);
        this.setIndustries(i ? [...i] : []);
        this.setOwnershipTypes(ot ? [...ot] : []);
        if (rmin !== undefined || rmax !== undefined) {
          const min = rmin !== undefined ? Number(rmin) : 0;
          const max = rmax !== undefined ? Number(rmax) : 0;
          this.setRevenueRange([min, max]);
        }
        if (frmin !== undefined || frmax !== undefined) {
          const min = frmin !== undefined ? Number(frmin) : 1;
          const max = frmax !== undefined ? Number(frmax) : 1000;
          this.setFortuneRankRange([min, max]);
        }
        this.searchValue = q || '';
        this.currentPage = (p && typeof p === 'string' && !isNaN(parseInt(p)) && parseInt(p)) || 0;
        this.sortField = s || '';
        this.sortDirection = d || '';
      }
    },
    searchCriteriaAsQuery() {
      const query = {};
      if (this.searchValue) query.q = this.searchValue;
      if (this.selectedAccountStatuses.length > 0) query.t = this.selectedAccountStatuses;
      if (this.selectedAgencies.length > 0) query.a = this.selectedAgencies;
      if (this.selectedSICIndustries.length > 0) query.si = this.selectedSICIndustries;
      if (this.selectedIndustries.length > 0) query.i = this.selectedIndustries;
      if (this.selectedCoverageTypes.length > 0) query.ct = this.selectedCoverageTypes;
      if (this.selectedOwnershipTypes.length > 0) query.ot = this.selectedOwnershipTypes;
      if (this.revenueMin !== undefined) query.rmin = this.revenueMin;
      if (this.revenueMax !== undefined) query.rmax = this.revenueMax;
      if (this.fortuneRankMin !== undefined) query.frmin = this.fortuneRankMin;
      if (this.fortuneRankMax !== undefined) query.frmax = this.fortuneRankMax;
      if (this.currentPage > 0) query.p = `${this.currentPage}`;
      if (this.sortField) query.s = this.sortField;
      if (this.sortDirection) query.d = this.sortDirection;
      return query;
    },
    async searchAccounts() {
      const query = this.searchCriteriaAsQuery();

      if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        await this.$router.replace({ query }).catch(() => {
          return '';
        });
      }

      const list = await accountService.search({
        search: this.searchValue,
        agencies: this.selectedAgencies,
        accountStatuses: this.selectedAccountStatuses,
        sicCodes: this.selectedSICIndustries,
        industryIds: this.selectedIndustries,
        coverageTypeIds: this.selectedCoverageTypes,
        ownershipType: this.selectedOwnershipTypes,
        limit: this.pageSize + 1,
        offset: this.currentPage * this.pageSize,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        showCurrentOnly: this.showCurrentOnly,
        revenueMin: this.revenueMin,
        revenueMax: this.revenueMax,
        fortuneRankMin: this.fortuneRankMin,
        fortuneRankMax: this.fortuneRankMax,
      });

      return list.map((account) => {
        // coverages
        const { coverageTypes: coverageTypeStr } = account;
        const coverageTypeDataList = coverageTypeStr.split(',').filter((t) => t !== '');
        const coverageTypes = coverageTypeDataList
          .map((coverageData) => {
            if (coverageData.includes('||')) {
              const [coverageType, isExternal] = coverageData.split('||');

              return { key: coverageData, coverageType, isExternal: isExternal.toLowerCase() === 'true' };
            } else {
              return { key: coverageData, coverageType: coverageData, isExternal: false };
            }
          })
          .sort((a, b) => (a.isExternal === b.isExternal ? 0 : a.isExternal ? 1 : -1));

        // agencies
        const agencies = account.agencies?.split(',');
        return { ...account, coverageTypes, coverageTypeStr, agencies };
      });
    },
    async refresh() {
      this.loading = true;
      this.currentPage = 0;

      try {
        /*
          A `q` param is added to the url if the user searches, sorts, or pages.
          If the user arrives back here by some other means besides clicking the
          back button (e.g. clicking the header), then the state will be reset
          and not restored.
        */
        this.setSearchCriteriaFromQuery();

        this.filteredList = await this.searchAccounts();
      } catch (err) {
        console.error(err);
        this.error = FailedToLoadPage;
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    await this.refresh();
    const [coverageTypes, clientIndustries, industries] = await Promise.all([
      referenceService.coverageTypes(),
      referenceService.sicCodes(),
      referenceService.industries(),
    ]);
    const maxRevenue = Math.max(0, ...(this.filteredList || []).map((a) => Number(a.revenue) || 0));
    this.filters = [
      ...this.filters,
      { category: 'Coverage Type', values: coverageTypes },
      { category: 'SIC Industry', values: clientIndustries.map((i) => ({ label: i.name, value: i.sicCode })) },
      { category: 'Industry', values: industries.map((i) => ({ label: i.name, value: i.ID })) },
      { category: 'Revenue', description: 'Filter accounts by revenue', values: [], range: [0, maxRevenue], prefix: '$' },
      { category: 'Fortune Rank', description: 'Filter accounts by Fortune Ranking', values: [], range: [1, 1000], prefix: '#' },
    ];
  },
  watch: {
    currentPage() {
      window.scrollTo(0, 0);
      this.filterAccounts();
    },
    showCurrentOnly() {
      this.filterAccounts();
    },
    totalOffset: {
      handler() {
        this.updateStickyTop();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* reka pins every ContextMenuTrigger to pointer-events:auto, which defeats the modal layer's
   body { pointer-events: none } for the other rows: the click that dismisses the menu would also
   follow the link or toggle the favorite underneath it. !important is required to beat reka's
   inline style. */
tbody.context-menu-open tr {
  pointer-events: none !important;
}

.page-header {
  display: grid;
  grid-template-columns: auto auto 1fr;
  grid-template-areas: 'header filter search-bar';
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;
}

.page-header > h1 {
  grid-area: header;
}

.page-header > .view-filter {
  grid-area: filter;
}
.page-header > .button-group.agencies {
  padding: 0.5rem 0;
}

@media (max-width: 1200px) {
  .page-header {
    grid-template-columns: auto auto 1fr;
    grid-template-areas: 'header header search-bar' 'filter filter filter';
  }

  .page-header > .button-group.account-status,
  .page-header > .button-group.agencies {
    padding: 0.5rem 0;
  }

  .page-header > .button-group.client-types {
    width: fit-content;
    justify-self: start;
    text-transform: uppercase;
  }

  .page-header > .search-bar {
    max-width: unset;
    justify-self: unset;
  }
}

@media (max-width: 600px) {
  .page-header {
    grid-template-columns: 1fr;
    grid-template-areas: 'header' 'filter' 'search-bar';
  }

  .page-header > .button-group.account-status {
    padding: 1rem 0 0;
  }

  .page-header > .button-group.agencies {
    padding: 1rem 0;
  }

  .page-header > .search-bar {
    width: 100%;
  }
}

.name-wrapper {
  max-width: 25em;
}

.name {
  font-weight: 500;
  letter-spacing: 0.02em;
  font-size: 1.1em;
}

.stock {
  display: grid;
  grid-template-columns: auto auto auto auto 1fr;
  grid-column-gap: 0.25rem;
  font-size: 0.9em;
  align-items: center;
  align-content: center;
}

.stock-ticker {
  font-weight: 600;
  color: var(--v-gray-darken1);
  letter-spacing: 0.02em;
}

.stock-exchange {
  color: var(--v-gray-base);
}

.industry-description {
  color: var(--v-gray-base);
}

.stock-public {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7em !important;
}

.prospect {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7em !important;
}

.empty {
  color: var(--v-gray-lighten3);
}

.chips {
  margin-top: 1em;
  max-width: 20vw;
}

thead {
  position: sticky;
  z-index: 2;
}
</style>
